import classNames from 'classnames'
import React, {
  ChangeEvent,
  DetailedHTMLProps,
  InputHTMLAttributes,
  ReactNode,
  useRef,
  useState,
} from 'react'
import Icon from '../icon'
import { ErrorText } from '../typography/ErrorText'

export type TextInputProps = {
  type?: string
  title?: string
  name?: string
  value?: string
  placeholder?: string
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
  ) => void
  hasIcon?: boolean
  iconSvg?: string
  iconDimension?: number
  inputClass?: string
  className?: string
  containerClassName?: string
  errors?: string
  rows?: number
  autoComplete?: 'off' | 'on'
  readOnly?: boolean
  showAsReadOnly?: boolean
  isDropdown?: boolean
  extra?: ReactNode
} & DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>

export const TextInput = ({
  type = 'text',
  title = '',
  name = '',
  value = '',
  placeholder = 'Placeholder',
  onChange = (val: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {},
  onBlur = () => {},
  hasIcon = true,
  iconSvg = '',
  iconDimension = 20,
  inputClass = '',
  className = '',
  containerClassName = '',
  errors = '',
  autoComplete = 'off',
  readOnly = false,
  showAsReadOnly = false,
  rows = 3,
  extra,
  isDropdown = false,
  ...inputProps
}: TextInputProps) => {
  const [showPassword, setShowPassword] = useState<boolean>(false)

  const inputRef = useRef<HTMLInputElement | null>(null)

  const inputClassName = `
          bg-[transparent] w-full
          outline-none focus:outline-none
          placeholder:text-neutral font-normal
          autofill:focus:bg-transparent autofill:bg-transparent
          text-base
          ${inputClass}
        `

  return (
    <div className={classNames('w-full', containerClassName)}>
      {title && (
        <p
          className="
            mb-2 text-base
            bg-white px-1
            font-medium
            text-black
        "
        >
          {title}
        </p>
      )}

      <div
        className={`
        w-full flex flex-row gap-4
        items-center border border-divider-gray
        transition-all duration-300
        rounded-md bg-transparent
        text-black p-4
        focus-within:!border-black
        group
        ${readOnly && showAsReadOnly && '!bg-bg-gray'}
        ${className}
      `}
      >
        {hasIcon && (
          <Icon
            id={iconSvg}
            width={iconDimension}
            height={iconDimension}
            className="min-w-fit"
          />
        )}
        {type === 'textarea' ? (
          <textarea
            name={name}
            value={value}
            placeholder={placeholder}
            onChange={onChange}
            onBlur={onBlur}
            autoComplete={autoComplete}
            readOnly={readOnly}
            className={inputClassName}
            rows={rows}
          />
        ) : (
          <input
            type={type === 'password' && showPassword ? 'text' : type}
            name={name}
            value={value}
            placeholder={placeholder}
            onChange={onChange}
            onBlur={onBlur}
            autoComplete={autoComplete}
            readOnly={readOnly}
            className={inputClassName}
            ref={inputRef}
            {...inputProps}
          />
        )}

        {extra && extra}

        {type === 'password' && (
          <button
            onClick={() => {
              setShowPassword(!showPassword)
            }}
            type="button"
          >
            <Icon
              id={showPassword ? 'hide' : 'show'}
              width={24}
              height={24}
              className="min-w-fit"
            />
          </button>
        )}

        {isDropdown ? (
          <button
            type="button"
            onClick={() => {
              const input = document.querySelector<HTMLInputElement>(
                `input[name="${name}"]`,
              )
              input?.click()
              input?.focus()
            }}
          >
            <Icon
              id={'left-caret'}
              width={16}
              height={16}
              className="-rotate-90 text-sec-black"
            />
          </button>
        ) : undefined}
      </div>

      {errors && <ErrorText text={errors} />}
    </div>
  )
}
